import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
	Box,
	Grid,
	TextField,
	MenuItem,
	Button,
	Typography,
} from '@mui/material';
import { IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { CommonButton } from '../../components/button/common-button';
import './sign-up.styles.css';
import {
	signUpUser,
	resendVerificationEmail,
} from '../../services/userService';
import axios from 'axios';
import { Snackbar, Alert } from '@mui/material';
import { getAge } from '../../utils/date/age';
import {
	REQUIRED_AGE,
	ErrorCodes,
	ErrorDescriptions,
	CREATED_MODAL_CONTENT,
} from '../../constants';
import { IMaskInput } from 'react-imask';
import React from 'react';
import { CUIT_CUIL_MASK, CELLPHONE_MASK } from '../../constants';
import {
	Checkbox,
	FormControlLabel,
	FormControl,
	FormHelperText,
	Modal,
} from '@mui/material';
import ModalComponent from '../../components/modal/modal.component';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import ReactMarkdown from 'react-markdown';
import { getProvinces, getMunicipalities } from '../../services/addressService';

enum Gender {
	FEMALE = 'Femenino',
	MALE = 'Masculino',
}

enum Nationality {
	ARGENTINIAN = 'Argentino',
	FOREIGNER = 'Extranjero',
}

enum DocumentType {
	DNI = 'DNI',
	PASSPORT = 'Pasaporte',
	OTHER = 'Otro',
}

const validationRules: {
	[key: string]: (value: string, formData: any) => string;
} = {
	firstName: (value) =>
		/^[A-Za-zÁáÉéÍíÓóÚúÑñ\s]+$/.test(value) ? '' : 'Solo letras permitidas',
	lastName: (value) =>
		/^[A-Za-zÁáÉéÍíÓóÚúÑñ\s]+$/.test(value) ? '' : 'Solo letras permitidas',
	documentNumber: (value, formData) => {
		if (formData.documentType === DocumentType.DNI) {
			return /^[0-9]+$/.test(value) ? '' : 'Solo números permitidos';
		}
		return /^[a-zA-Z0-9\s\-\_]+$/.test(value) ? '' : 'Caracteres no permitidos';
	},
	cuitCuil: (value, formData) => {
		const numericValue = value.replace(/\D/g, '');

		const validPrefixes = ['20', '23', '24', '27'];
		const prefix = numericValue.slice(0, 2);

		if (formData.documentType === DocumentType.DNI) {
			if (!validPrefixes.includes(prefix)) {
				return 'Prefijo de CUIT/CUIL inválido';
			}

			const formattedDni =
				formData.documentNumber.length === 7
					? '0' + formData.documentNumber
					: formData.documentNumber;
			const cuitCuilDocumentNumber = numericValue.slice(2, 10);
			if (cuitCuilDocumentNumber !== formattedDni) {
				return 'El CUIT/CUIL debe coincidir con el número de documento';
			}

			const isValidCheckDigit = validateMod11(numericValue);
			if (!isValidCheckDigit) {
				return 'El dígito verificador de CUIT/CUIL es incorrecto';
			}
		}

		return '';
	},
	birthDate: (value) => {
		return getAge(value) >= REQUIRED_AGE
			? ''
			: `Debes ser mayor de ${REQUIRED_AGE} años para registrarte`;
	},
	address: (value) =>
		/^(?=.{5,})/.test(value)
			? ''
			: 'La dirección debe tener al menos 5 caracteres',
	email: (value) =>
		/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Formato de email inválido',
	password: (value) =>
		/^(?=.{8,})/.test(value)
			? ''
			: 'La contraseña debe tener al menos 8 caracteres',
	confirmPassword: (value, formData) =>
		value === formData.password
			? value.length < 8
				? 'La contraseña debe tener al menos 8 caracteres'
				: ''
			: 'Las contraseñas no coinciden',
	acceptedTerms: (value) =>
		value ? '' : 'Debes aceptar los Términos y Condiciones para registrarte',
};

const validateMod11 = (cuitCuil: string): boolean => {
	const numericValue = cuitCuil.replace(/\D/g, '');

	if (numericValue.length !== 11) return false;

	const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

	let sum = 0;
	for (let i = 0; i < weights.length; i++) {
		sum += parseInt(numericValue[i]) * weights[i];
	}

	const mod11Result = sum % 11;
	const expectedCheckDigit = mod11Result === 0 ? 0 : 11 - mod11Result;

	return expectedCheckDigit === parseInt(numericValue[10]);
};

const validateInput = (name: string, value: string, formData: any): string => {
	return validationRules[name] ? validationRules[name](value, formData) : '';
};

const CuitCuilMask = React.forwardRef<HTMLInputElement, any>((props, ref) => {
	const { onChange, ...other } = props;
	return (
		<IMaskInput
			{...other}
			mask={CUIT_CUIL_MASK}
			placeholder={CUIT_CUIL_MASK}
			definitions={{
				'#': /[0-9]/,
			}}
			inputRef={ref}
			onAccept={(value: any) =>
				onChange({ target: { name: props.name, value } })
			}
			overwrite
		/>
	);
});

const CellphoneMask = React.forwardRef<HTMLInputElement, any>((props, ref) => {
	const { onChange, ...other } = props;
	return (
		<IMaskInput
			{...other}
			mask={CELLPHONE_MASK}
			placeholder={CELLPHONE_MASK}
			definitions={{
				'0': /[0-9]/,
			}}
			inputRef={ref}
			onAccept={(value: any) =>
				onChange({ target: { name: props.name, value } })
			}
			overwrite
		/>
	);
});

const SignUpPage = () => {
	const navigate = useNavigate();

	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		gender: '',
		nationality: '',
		documentType: '',
		documentNumber: '',
		cuitCuil: '',
		birthDate: '',
		phoneNumber: '',
		address: '',
		province: '',
		city: '',
		email: '',
		password: '',
		confirmPassword: '',
		addressInfo: '',
		acceptedTerms: false,
	});

	const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
	const [touchedFields, setTouchedFields] = useState<{
		[key: string]: boolean;
	}>({});

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, checked } = e.target;

		setTouchedFields((prev) => ({ ...prev, [name]: true }));

		if (name === 'acceptedTerms') {
			setFormData((prevData) => ({
				...prevData,
				[name]: checked,
				acceptedTermsId: checked ? termsVersion : null,
			}));
		} else if (name === 'nationality' && value === Nationality.ARGENTINIAN) {
			setFormData((prevData) => ({
				...prevData,
				nationality: Nationality.ARGENTINIAN,
				documentType: DocumentType.DNI,
			}));
		} else {
			setFormData((prevData) => ({
				...prevData,
				[name]: value,
			}));
		}
	};

	const [municipalities, setMunicipalities] = useState<any[]>([]);
	const handleProvinceChange = async (
		event: React.ChangeEvent<{ value: unknown }>,
	) => {
		const value = event.target.value as string;
		try {
			const data = await getMunicipalities(value);
			setMunicipalities(data);
		} catch (error) {
			console.error('Error fetching municipalities:', error);
		}
		setFormData((prevData) => ({
			...prevData,
			province: value,
		}));
		setTouchedFields((prev) => ({ ...prev, province: true }));
	};

	const handleMunicipalityChange = async (
		event: React.ChangeEvent<{ value: unknown }>,
	) => {
		const value = event.target.value as string;

		setFormData((prevData) => ({
			...prevData,
			city: value,
		}));
		setTouchedFields((prev) => ({ ...prev, city: true }));
	};

	useEffect(() => {
		const updatedErrors = validateErrors(formData);
		setFormErrors(updatedErrors);
	}, [formData]);

	const validateErrors = (formData: any, validateTerms = false) => {
		const errors: { [key: string]: string } = {};

		Object.keys(formData).forEach((key) => {
			if (touchedFields[key]) {
				const errorMessage = validateInput(key, formData[key], formData);
				if (errorMessage) {
					errors[key] = errorMessage;
				}
			}
		});

		if (validateTerms && !formData.acceptedTerms) {
			const errorMessage = validationRules.acceptedTerms(
				formData.acceptedTerms,
				formData,
			);
			errors.acceptedTerms = errorMessage;
		}
		return errors;
	};

	const isDocumentTypeDisabled =
		formData.nationality === Nationality.ARGENTINIAN;

	const [showPassword, setShowPassword] = useState<boolean>(false);

	const handleClickShowPassword = () => setShowPassword(!showPassword);
	const handleMouseDownPassword = (
		event: React.MouseEvent<HTMLButtonElement>,
	) => {
		event.preventDefault();
	};

	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');

	const [modalTermsOpen, setModalTermsOpen] = useState(false);
	const handleOpenTermsAndConditions = (e: React.MouseEvent) => {
		e.preventDefault();
		setModalTermsOpen(true);
	};
	const handleCloseTermsAndConditions = () => setModalTermsOpen(false);

	const [termsContent, setTermsContent] = useState<string>('');
	const [termsVersion, setTermsVersion] = useState<string>('');

	useEffect(() => {
		axios
			.get(`${process.env.REACT_APP_BACKEND}/terms-conditions/latest`)
			.then((res) => {
				setTermsContent(res.data.content);
				setTermsVersion(res.data.id);
			})
			.catch((err) => {
				console.error('Error fetching terms:', err);
				setTermsContent('Error al cargar los términos y condiciones.');
			});
	}, []);
	const [provinces, setProvinces] = useState<any[]>([]);
	useEffect(() => {
		const fetchProvinces = async () => {
			try {
				const data = await getProvinces();
				setProvinces(data);
			} catch (error) {
				console.error('Error fetching provinces:', error);
			}
		};
		fetchProvinces();
	}, []);

	const [modalSuccessOpen, setModalSuccessOpen] = useState(false);

	const handleResendEmail = async () => {
		try {
			await resendVerificationEmail();
		} catch (error) {
			console.error('Error resending verification email:', error);
		}
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		const errors = validateErrors(formData, true);
		setFormErrors(errors);

		const hasErrors = Object.values(errors).some((error) => error !== '');

		if (hasErrors) {
			console.error('Form contains errors. Submission prevented.');
			return;
		}

		try {
			const cleanedCuitCuil = formData.cuitCuil.replace(/\D/g, '');
			const { confirmPassword, ...dataToSend } = formData;
			const fixedData = {
				...dataToSend,
				cellphone: dataToSend.phoneNumber,
				cuitCuil: cleanedCuitCuil,
				role: 'USER',
			};

			const result = await signUpUser(fixedData);

			const auth = getAuth();
			await signInWithEmailAndPassword(auth, formData.email, formData.password);

			setFormData({
				firstName: '',
				lastName: '',
				gender: '',
				nationality: '',
				documentType: '',
				documentNumber: '',
				cuitCuil: '',
				birthDate: '',
				phoneNumber: '',
				address: '',
				province: '',
				city: '',
				email: '',
				password: '',
				confirmPassword: '',
				addressInfo: '',
				acceptedTerms: false,
			});

			setFormErrors({});
			setTouchedFields({});

			setModalSuccessOpen(true);
		} catch (error) {
			if (axios.isAxiosError(error)) {
				console.error('Error signing up:', error.response?.data);

				const errorCode =
					error.response?.data?.errorDetails?.response?.errorCode;

				if (errorCode === ErrorCodes[ErrorCodes.DUPLICATED_USER]) {
					setAlertMessage(ErrorDescriptions.DUPLICATED_USER);
				} else {
					setAlertMessage(ErrorDescriptions.UNEXPECTED);
				}

				setAlertOpen(true);
			} else {
				console.error('Unexpected error:', error);
				setAlertMessage(ErrorDescriptions.UNEXPECTED);
				setAlertOpen(true);
			}
		}
	};

	const styles = {
		box: {
			maxWidth: 600,
			margin: 'auto',
			mt: 5,
			border: '1px solid #ddd',
			borderRadius: 2,
			padding: 3,
			boxShadow: 3,
		},
		modalBox: {
			position: 'absolute',
			top: '50%',
			left: '50%',
			transform: 'translate(-50%, -50%)',
			width: { xs: '90%', sm: '80%', md: '600px' },
			bgcolor: 'background.paper',
			boxShadow: 24,
			p: { xs: 2, sm: 4 },
			borderRadius: 2,
			maxHeight: '80vh',
			overflowY: 'auto',
		},
		modalCloseButton: {
			position: 'absolute',
			top: 8,
			right: 8,
			color: 'grey.700',
		},
		modalTitle: { fontSize: { xs: '18px', sm: '22px' }, textAlign: 'center' },
		modalTextMain: { mt: 2, fontSize: { xs: '18px', sm: '20px' } },
		modalTextSecondary: { mt: 2, fontSize: { xs: '14px', sm: '16px' } },
	};

	return (
		<>
			<Box className="main-container">
				<CommonButton action={() => navigate('/')} margin="12px 0">
					{'< Volver'}
				</CommonButton>
				<Box className="header-container">
					<img src={'/cambiapp_logo.png'} alt="Logo" />
				</Box>
				<Box className="body-container">
					<Box sx={styles.box}>
						<form onSubmit={handleSubmit}>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<TextField
										label="Nombre"
										name="firstName"
										value={formData.firstName}
										onChange={handleChange}
										required
										error={Boolean(formErrors.firstName)}
										helperText={
											<span className="error-text">{formErrors.firstName}</span>
										}
										fullWidth
										margin="normal"
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										label="Apellido"
										name="lastName"
										value={formData.lastName}
										onChange={handleChange}
										required
										error={Boolean(formErrors.lastName)}
										helperText={
											<span className="error-text">{formErrors.lastName}</span>
										}
										fullWidth
										margin="normal"
									/>
								</Grid>

								<Grid item xs={12} sm={6}>
									<TextField
										select
										label="Nacionalidad"
										name="nationality"
										value={formData.nationality}
										onChange={handleChange}
										required
										fullWidth
										margin="normal"
									>
										{Object.values(Nationality).map((option) => (
											<MenuItem key={option} value={option}>
												{option}
											</MenuItem>
										))}
									</TextField>
								</Grid>

								<Grid item xs={12} sm={6}>
									<TextField
										select
										label="Género"
										name="gender"
										value={formData.gender}
										onChange={handleChange}
										required
										fullWidth
										margin="normal"
									>
										{Object.values(Gender).map((option) => (
											<MenuItem key={option} value={option}>
												{option}
											</MenuItem>
										))}
									</TextField>
								</Grid>

								<Grid item xs={12} sm={6}>
									<TextField
										select
										label="Tipo de documento"
										name="documentType"
										value={formData.documentType}
										onChange={handleChange}
										required
										fullWidth
										margin="normal"
										disabled={isDocumentTypeDisabled}
									>
										{Object.values(DocumentType).map((option) => (
											<MenuItem key={option} value={option}>
												{option}
											</MenuItem>
										))}
									</TextField>
								</Grid>

								<Grid item xs={12} sm={6}>
									<TextField
										label="Número de documento"
										name="documentNumber"
										value={formData.documentNumber}
										onChange={handleChange}
										required
										error={Boolean(formErrors.documentNumber)}
										helperText={
											<span className="error-text">
												{formErrors.documentNumber}
											</span>
										}
										fullWidth
										margin="normal"
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										label="CUIT/CUIL"
										name="cuitCuil"
										value={formData.cuitCuil}
										onChange={handleChange}
										required={formData.documentType === DocumentType.DNI}
										error={Boolean(formErrors.cuitCuil)}
										helperText={
											<span className="error-text">{formErrors.cuitCuil}</span>
										}
										fullWidth
										margin="normal"
										InputProps={{
											inputComponent: CuitCuilMask as any,
										}}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										label="Fecha de nacimiento"
										name="birthDate"
										type="date"
										value={formData.birthDate}
										onChange={handleChange}
										required
										error={Boolean(formErrors.birthDate)}
										helperText={
											<span className="error-text">{formErrors.birthDate}</span>
										}
										fullWidth
										margin="normal"
										InputLabelProps={{
											shrink: true,
										}}
										className="custom-date"
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										label="Celular"
										name="phoneNumber"
										value={formData.phoneNumber}
										onChange={handleChange}
										required
										helperText={
											<span className="helper-text">
												Ingresar cód. de área sin 0 + número sin 15
											</span>
										}
										fullWidth
										margin="normal"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">+54 9</InputAdornment>
											),
											inputComponent: CellphoneMask as any,
										}}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										select
										label="Provincia"
										name="province"
										value={formData.province}
										onChange={handleProvinceChange}
										required
										fullWidth
										margin="normal"
									>
										{provinces.map((province) => (
											<MenuItem key={province.id} value={province.nombre}>
												{province.nombre}
											</MenuItem>
										))}
									</TextField>
								</Grid>
								<Grid item xs={12}>
									<TextField
										select
										label="Ciudad"
										name="municipality"
										value={formData.city}
										onChange={handleMunicipalityChange}
										required
										fullWidth
										margin="normal"
									>
										{municipalities.map((municipality) => (
											<MenuItem
												key={municipality.id}
												value={municipality.nombre}
											>
												{municipality.nombre}
											</MenuItem>
										))}
									</TextField>
								</Grid>
								<Grid item xs={12}>
									<TextField
										label="Dirección"
										name="address"
										value={formData.address}
										onChange={handleChange}
										required
										error={Boolean(formErrors.address)}
										helperText={
											formErrors.address ? (
												<span className="error-text">{formErrors.address}</span>
											) : (
												<span className="helper-text">
													Ingresar calle y número, datos catastrales, o ruta y
													km
												</span>
											)
										}
										fullWidth
										margin="normal"
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										label="Email"
										name="email"
										type="email"
										value={formData.email}
										onChange={handleChange}
										required
										error={Boolean(formErrors.email)}
										helperText={
											<span className="error-text">{formErrors.email}</span>
										}
										placeholder="example@email.com"
										fullWidth
										margin="normal"
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										label="Contraseña"
										name="password"
										type={showPassword ? 'text' : 'password'}
										value={formData.password}
										onChange={handleChange}
										required
										error={Boolean(formErrors.password)}
										helperText={
											<span className="error-text">{formErrors.password}</span>
										}
										fullWidth
										margin="normal"
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={handleClickShowPassword}
														onMouseDown={handleMouseDownPassword}
													>
														{showPassword ? <Visibility /> : <VisibilityOff />}
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										label="Confirmar contraseña"
										name="confirmPassword"
										type={showPassword ? 'text' : 'password'}
										value={formData.confirmPassword}
										onChange={handleChange}
										required
										error={Boolean(formErrors.confirmPassword)}
										helperText={
											<span className="error-text">
												{formErrors.confirmPassword}
											</span>
										}
										fullWidth
										margin="normal"
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={handleClickShowPassword}
														onMouseDown={handleMouseDownPassword}
													>
														{showPassword ? <Visibility /> : <VisibilityOff />}
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
								</Grid>
								<Grid item xs={12}>
									<FormControl
										required
										error={Boolean(formErrors.acceptedTerms)}
										fullWidth
										margin="normal"
									>
										<FormControlLabel
											control={
												<Checkbox
													checked={formData.acceptedTerms}
													onChange={handleChange}
													name="acceptedTerms"
													color="primary"
												/>
											}
											label={
												<>
													Acepto los{' '}
													<a href="#" onClick={handleOpenTermsAndConditions}>
														Términos y Condiciones
													</a>
												</>
											}
										/>
										{formErrors.acceptedTerms && (
											<FormHelperText>
												<span className="error-text">
													{formErrors.acceptedTerms}
												</span>
											</FormHelperText>
										)}
									</FormControl>
								</Grid>
								<Modal
									open={modalTermsOpen}
									onClose={handleCloseTermsAndConditions}
									aria-labelledby="terms-modal-title"
								>
									<Box sx={styles.modalBox}>
										<IconButton
											onClick={handleCloseTermsAndConditions}
											sx={styles.modalCloseButton}
										>
											✖
										</IconButton>

										<ReactMarkdown
											components={{
												h1: ({ children }) => (
													<Typography sx={styles.modalTitle}>
														{children}
													</Typography>
												),
												h2: ({ children }) => (
													<Typography
														variant="h6"
														sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}
													>
														{children}
													</Typography>
												),
												p: ({ children }) => (
													<Typography variant="body1" sx={styles.modalTextMain}>
														{children}
													</Typography>
												),
												li: ({ children }) => (
													<Typography
														component="li"
														variant="body2"
														sx={styles.modalTextSecondary}
													>
														{children}
													</Typography>
												),
												ul: ({ children }) => (
													<ul
														style={{
															paddingLeft: '1.5rem',
															marginTop: '0.5rem',
														}}
													>
														{children}
													</ul>
												),
												ol: ({ children }) => (
													<ol
														style={{
															paddingLeft: '1.5rem',
															marginTop: '0.5rem',
														}}
													>
														{children}
													</ol>
												),
											}}
										>
											{termsContent}
										</ReactMarkdown>
									</Box>
								</Modal>
								<Grid item xs={12}>
									<Button
										type="submit"
										variant="contained"
										color="primary"
										fullWidth
										sx={{ mt: 2 }}
									>
										Crear Cuenta
									</Button>
								</Grid>
								<ModalComponent
									open={modalSuccessOpen}
									title={CREATED_MODAL_CONTENT.title}
									description={CREATED_MODAL_CONTENT.description}
									imageSrc="/cambiapp_logo.png"
									buttonText="Volver al inicio"
									onButtonClick={() => navigate('/login')}
									onClose={() => navigate('/login')}
									secondaryButtonText="Reenviar email"
									onSecondaryClick={() => handleResendEmail()}
								/>
							</Grid>
						</form>
					</Box>
				</Box>
				<Snackbar
					open={alertOpen}
					autoHideDuration={6000}
					onClose={() => setAlertOpen(false)}
					anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				>
					<Alert
						onClose={() => setAlertOpen(false)}
						severity="error"
						sx={{ width: '100%' }}
					>
						{alertMessage}
					</Alert>
				</Snackbar>
			</Box>
		</>
	);
};

export default SignUpPage;
